import { createFileRoute } from '@tanstack/react-router';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import {
  useGetBidComparison,
  useCreateBidComparisonColumn,
  useRefreshBidsComparison,
  useBidComparisonRetriggerMutation,
} from '@/app/hooks/queries/useBidComparisons';
import { useGetAllBidsIndexingStatus } from '@/app/hooks/useAllBidsIndexingStatus';
import {
  BidComparisonColumnStatus,
  DocumentTypes,
  EvaluationTypeLabelsEnum,
  ProxyDocumentTypes,
  queryKeys,
} from '@/app/utils/constants';
import useEntityDocuments from '@/app/hooks/useEntityDocuments';
import useDocumentsIndexingStatus from '@/app/hooks/useDocumentsIndexingStatus';
import { FloatingAlert } from '@/app/components/shared/FloatingAlert';
import {
  MoreHorizontalIcon,
  AuctionIcon,
  ArrowDataTransferHorizontalIcon,
  PlusSignIcon,
  EyeIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { Button } from '@/app/ui/components/button';
import { useMemo, useState, useEffect } from 'react';
import { Spinner } from '@/app/ui/components/spinner';
import { Menu, MenuItem, MenuPopup, MenuTrigger } from '@/app/ui/components/menu';
import { useAtom, useSetAtom } from 'jotai';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/ui/components/table';
import { Badge } from '@/app/ui/components/badge';
import { BidComparisonBid, BidComparisonColumn } from '@/app/types/models';
import {
  Dialog,
  DialogPopup,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTitleSeparator,
  DialogTitleBreadcrumb,
  DialogXClose,
  DialogContent,
  DialogCancel,
  DialogScrollArea,
} from '@/app/ui/components/dialog';
import CitationListItem from '@/app/components/shared/Citations/CitationListItem';
import NewBidDraftingMarkdown from '@/app/components/features/evaluations/analysis/BidDrafting/NewBidDraftingMarkdown';
import { Field, FieldControl, FieldError, FieldLabel } from '@/app/ui/components/form/field';
import { Textarea } from '@/app/ui/components/textarea';
import { toastManager } from '@/app/ui/components/toast';
import { ExpandableText } from '@/app/components/features/workPackages/ExpandableText';
import CellLoader from '@/app/components/features/workPackages/CellLoader';
import EmptyCellPlaceholder from '@/app/components/features/workPackages/EmptyCellPlaceholder';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderBackButton,
  HeaderMobileChatButton,
  HeaderSidebarTrigger,
  HeaderSidechatButton,
} from '@/app/ui/components/header';
import {
  MainActions,
  MainHeader,
  MainHeaderBackButton,
  MainScrollArea,
} from '@/app/ui/components/main';
import { H1 } from '@/app/ui/components/heading';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from '@/app/ui/components/empty';
import { BidsIcon } from '@/app/components/shared/EmptyIcons';
import { CreateBid } from '@/app/components/features/bids/CreateBid/CreateBid';
import { createBidOpenAtom } from '@/app/components/features/bids/store';
import { Form } from '@/app/ui/components/form';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import {
  addColumnDialogOpenAtom,
  userQueryAtom,
} from '@/app/components/features/bidComparison/store';
import { BidComparisonColumnsMenu } from '@/app/components/features/bidComparison/BidComparisonColumnsMenu';
import { usePersistedBidComparisonColumns } from '@/app/components/features/bidComparison/usePersistedBidComparisonColumns';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/bids/_layout/compare',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.bids');
  const tEvaluation = useTranslations('pages.evaluation');
  const tComparisonTable = useTranslations('components.comparisonTable');
  const tGeneral = useTranslations('general');
  const { tenderPackageId, projectId } = Route.useParams();
  const setCreateBidOpen = useSetAtom(createBidOpenAtom);

  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));
  const { data: bidComparison, isLoading: isLoadingBidComparison } =
    useGetBidComparison(tenderPackageId);
  const { mutateAsync: createColumn, isPending: isCreatingColumn } =
    useCreateBidComparisonColumn(tenderPackageId);

  const [addColumnDialogOpen, setAddColumnDialogOpen] = useAtom(addColumnDialogOpenAtom);
  const [userQuery, setUserQuery] = useAtom(userQueryAtom);
  const { allColumns, visibleColumns, setVisibleColumns, initializeColumns } =
    usePersistedBidComparisonColumns(tenderPackageId);
  const [selectedColumn, setSelectedColumn] = useState<{
    bid: BidComparisonBid;
    column: BidComparisonColumn;
    userQuery: string;
  } | null>(null);

  const hasInProgressColumns =
    bidComparison?.bids.some((bid) =>
      bid.columns.some((col) => col.status === BidComparisonColumnStatus.IN_PROGRESS),
    ) ?? false;

  useRefreshBidsComparison(tenderPackageId, hasInProgressColumns);

  // Stale analysis handling
  const queryClient = useQueryClient();
  const { data: documents } = useEntityDocuments(tenderPackageId, DocumentTypes.TENDER_PACKAGE);
  const hasTenderPackageDocs = documents && documents.length > 0;
  const { data: tpIndexingStatus } = useDocumentsIndexingStatus({
    documentType: ProxyDocumentTypes.TENDER_PACKAGE,
    id: tenderPackageId,
    enabled: hasTenderPackageDocs,
  });
  const tpIndexed = (tpIndexingStatus?.indexedPercentage ?? 0) === 100;

  const { allBidsIndexed } = useGetAllBidsIndexingStatus(tenderPackageId);

  const areDocumentsIndexed = tpIndexed && allBidsIndexed;

  const versionStatus = bidComparison?.versionStatus;
  const { mutate: retriggerComparison, isPending: isRetriggering } =
    useBidComparisonRetriggerMutation(tenderPackageId);

  useEffect(() => {
    if (versionStatus === 'stale') {
      void queryClient.invalidateQueries({
        queryKey: [queryKeys.INDEXING_STATUS, ProxyDocumentTypes.TENDER_PACKAGE, tenderPackageId],
      });
    }
  }, [versionStatus, queryClient, tenderPackageId]);

  // build columns from bid comparison data
  const initialColumns = useMemo(() => {
    if (!bidComparison?.bids || bidComparison.bids.length === 0) return [];
    const querySet = new Set<string>();
    bidComparison.bids.forEach((bid) => {
      bid.columns.forEach((col) => {
        querySet.add(col.userQuery);
      });
    });
    return Array.from(querySet).map((query) => ({
      id: query,
      title: query,
    }));
  }, [bidComparison]);

  // initialize columns when data changes
  useEffect(() => {
    initializeColumns(initialColumns);
  }, [initialColumns, initializeColumns]);

  const toggleColumn = (columnQuery: string) => {
    setVisibleColumns((prev) => {
      const isVisible = prev.some((col) => col.id === columnQuery);
      if (isVisible) {
        return prev.filter((col) => col.id !== columnQuery);
      } else {
        const columnToAdd = allColumns.find((col) => col.id === columnQuery);
        if (!columnToAdd) return prev;

        const newVisible = [...prev];
        const targetIndex = allColumns.findIndex((col) => col.id === columnQuery);

        let insertIndex = 0;
        for (let i = 0; i < newVisible.length; i++) {
          const visibleColIndex = allColumns.findIndex((col) => col.id === newVisible[i].id);
          if (visibleColIndex < targetIndex) {
            insertIndex = i + 1;
          }
        }

        newVisible.splice(insertIndex, 0, columnToAdd);
        return newVisible;
      }
    });
  };

  const handleAddColumn = async () => {
    if (!userQuery.trim()) return;

    try {
      const query = userQuery.trim();
      await createColumn({ userQuery: query });
      setUserQuery('');
      setAddColumnDialogOpen(false);
      toastManager.add({
        type: 'success',
        title: tEvaluation('notifications.columnAdded'),
      });
    } catch {
      toastManager.add({
        type: 'error',
        title: tGeneral('somethingWentWrong'),
      });
    }
  };

  const handleCellClick = (
    bid: BidComparisonBid,
    column: BidComparisonColumn,
    userQuery: string,
  ) => {
    setSelectedColumn({ bid, column, userQuery });
  };

  const hasBids = bidComparison?.bids && bidComparison.bids.length > 0;

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids"
                params={{ projectId, tenderPackageId }}
              >
                <HugeiconsIcon icon={AuctionIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                <HugeiconsIcon icon={ArrowDataTransferHorizontalIcon} />
                {tEvaluation('heading')}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
        <HeaderMobileChatButton />
      </Header>
      <MainScrollArea>
        {isLoadingBidComparison ? (
          <Spinner kind="inset" />
        ) : !hasBids ? (
          <Empty>
            <EmptyMedia>
              <BidsIcon />
            </EmptyMedia>
            <EmptyContent>
              <EmptyTitle>{t('empty.title')}</EmptyTitle>
              <EmptyDescription>{t('empty.description')}</EmptyDescription>
              <Button kind="with-icon" onClick={() => setCreateBidOpen(true)}>
                <HugeiconsIcon icon={PlusSignIcon} />
                {t('buttons.newBid')}
              </Button>
            </EmptyContent>
          </Empty>
        ) : (
          <>
            <MainHeader className="max-md:flex">
              <H1 className="max-md:hidden">
                <MainHeaderBackButton />
                {tEvaluation('heading')}
              </H1>
              <Button
                kind="with-icon"
                className="max-sm:w-full"
                onClick={() => setAddColumnDialogOpen(true)}
              >
                <HugeiconsIcon icon={PlusSignIcon} />
                {tComparisonTable('buttons.addColumn')}
              </Button>
            </MainHeader>
            <MainActions className="flex px-x max-md:pb-3.5 border-b md:border-y mb-0">
              <BidComparisonColumnsMenu tenderPackageId={tenderPackageId} />
            </MainActions>
            <Table className="flex flex-col [--column-width:18.5rem] @7xl:[--padding-x:0.9rem]">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="min-w-(--column-width) max-w-(--column-width) @7xl:sticky relative left-0 bg-white after:block after:top-0 after:bg-neutral after:h-full after:w-px after:absolute after:right-0 z-10">
                    {tEvaluation('table.headers.clientsSuppliers')}
                  </TableHead>
                  {visibleColumns.map((column) => (
                    <TableHead
                      key={column.id}
                      className="min-w-(--column-width) max-w-(--column-width) bg-white relative after:block after:top-0 after:bg-neutral after:h-full after:w-px after:absolute after:right-0"
                    >
                      <div className="flex items-center gap-2">
                        <span className="truncate">{column.title}</span>
                        <Menu>
                          <MenuTrigger
                            render={
                              <Button kind="icon" variant="ghost" size="sm" className="ml-auto">
                                <HugeiconsIcon
                                  icon={MoreHorizontalIcon}
                                  className="size-5.5 md:size-5"
                                />
                              </Button>
                            }
                          />
                          <MenuPopup align="end">
                            <MenuItem onClick={() => toggleColumn(column.id)}>
                              <HugeiconsIcon icon={EyeIcon} className="size-4" />
                              {tComparisonTable('buttons.hideColumn')}
                            </MenuItem>
                          </MenuPopup>
                        </Menu>
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {bidComparison?.bids.map((bid) => (
                  <TableRow key={bid.bidId} className="hover:bg-transparent group/row">
                    <TableCell className="@7xl:sticky group-last/row:border-b relative left-0 bg-white after:block after:top-0 after:bg-neutral after:h-full after:w-px after:absolute after:right-0 z-10 min-w-(--column-width) max-w-(--column-width) align-top">
                      <div className="h-full min-h-70">
                        <Badge
                          variant="tertiary"
                          className="w-full max-w-fit justify-start gap-2 py-1.5 px-3"
                        >
                          <HugeiconsIcon icon={AuctionIcon} className="size-4 shrink-0" />
                          <span className="truncate">{bid.companyName}</span>
                        </Badge>
                      </div>
                    </TableCell>
                    {visibleColumns.map((column) => {
                      const bidColumn = bid.columns.find((col) => col.userQuery === column.id);
                      const isInProgress =
                        bidColumn?.status === BidComparisonColumnStatus.IN_PROGRESS;
                      const isCompleted = bidColumn?.status === BidComparisonColumnStatus.DONE;
                      const statementCount = bidColumn?.statements?.length || 0;

                      return (
                        <TableCell
                          key={column.id}
                          className="group min-w-(--column-width) max-w-(--column-width) group-last/row:border-b relative after:block after:top-0 after:bg-neutral after:h-full after:w-px after:absolute after:right-0 align-top"
                        >
                          <div className="h-full min-h-70 flex flex-col">
                            {isInProgress && <CellLoader />}
                            {isCompleted && !bidColumn?.summary && <EmptyCellPlaceholder />}
                            {bidColumn && bidColumn.summary && (
                              <>
                                <div className="flex-1">
                                  <ExpandableText text={bidColumn.summary} />
                                </div>
                                <div className="flex items-end justify-between gap-2 text-sm text-muted mt-4">
                                  <div className="flex flex-col gap-1.5">
                                    <span
                                      className="cursor-pointer hover:underline transition-colors text-blue-600"
                                      onClick={() => handleCellClick(bid, bidColumn, column.id)}
                                    >
                                      {tEvaluation('table.labels.showReferences', {
                                        count: statementCount,
                                      })}
                                    </span>
                                  </div>
                                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={() => handleCellClick(bid, bidColumn, column.id)}
                                    >
                                      {tEvaluation('table.labels.open')}
                                    </Button>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </MainScrollArea>

      <Dialog open={addColumnDialogOpen} onOpenChange={setAddColumnDialogOpen}>
        <DialogPopup>
          <DialogTitle>
            <DialogTitleBreadcrumb>{tEvaluation('heading')}</DialogTitleBreadcrumb>
            <DialogTitleSeparator />
            <DialogTitleBreadcrumb>{tEvaluation('addColumnModal.title')}</DialogTitleBreadcrumb>
            <DialogXClose />
          </DialogTitle>
          <DialogContent render={<Form onFormSubmit={handleAddColumn} />}>
            <DialogScrollArea>
              <DialogDescription>{tEvaluation('addColumnModal.description')}</DialogDescription>
              <Field className={'mt-2'}>
                <FieldLabel>{tEvaluation('addColumnModal.inputLabel')}</FieldLabel>
                <FieldControl
                  value={userQuery}
                  onValueChange={setUserQuery}
                  placeholder={tEvaluation('addColumnModal.inputPlaceholder')}
                  required
                  render={<Textarea minRows={4} maxRows={10} />}
                />
                <FieldError />
              </Field>
            </DialogScrollArea>
            <DialogFooter>
              <DialogCancel />
              <Button
                type="submit"
                pending={isCreatingColumn}
                disabled={!userQuery.trim() || isCreatingColumn}
              >
                {tEvaluation('buttons.addColumn')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </DialogPopup>
      </Dialog>

      <Dialog open={!!selectedColumn} onOpenChange={(open) => !open && setSelectedColumn(null)}>
        <DialogPopup size="2xl" className="md:h-150">
          <DialogTitle>
            <DialogTitleBreadcrumb>{selectedColumn?.bid.companyName}</DialogTitleBreadcrumb>
            <DialogTitleSeparator />
            <DialogTitleBreadcrumb className={'line-clamp-1'}>
              {selectedColumn?.userQuery}
            </DialogTitleBreadcrumb>
            <DialogXClose />
          </DialogTitle>
          <DialogContent>
            <DialogScrollArea>
              {selectedColumn?.column.summary && (
                <div className="mb-6">
                  <h3 className="font-medium mb-2">{tEvaluation('detailsModal.tabs.summary')}</h3>
                  <NewBidDraftingMarkdown
                    className="new-prose-sm"
                    text={selectedColumn.column.summary}
                  />
                </div>
              )}
              {selectedColumn?.column.statements && selectedColumn.column.statements.length > 0 && (
                <div>
                  <h3 className="font-medium mb-2">
                    {tEvaluation('detailsModal.tabs.references', {
                      count: selectedColumn.column.statements.length,
                    })}
                  </h3>
                  <div className="divide-y">
                    {selectedColumn.column.statements.map((statement, idx) => (
                      <div key={idx} className="py-3">
                        {statement.documentFilename && (
                          <div className="flex items-center gap-2 mb-2 text-sm">
                            <Badge>
                              <CitationListItem
                                title={statement.documentFilename}
                                documentId={statement.docId || ''}
                                source={statement.statement}
                                content={statement.statement}
                                pageStart={statement.page ?? undefined}
                                useProjectDocuments={false}
                              />
                            </Badge>
                          </div>
                        )}
                        <NewBidDraftingMarkdown
                          className="new-prose-sm"
                          text={statement.statement}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </DialogScrollArea>
          </DialogContent>
        </DialogPopup>
      </Dialog>
      <CreateBid />
      {versionStatus === 'stale' && !hasInProgressColumns && (
        <FloatingAlert
          type="warning"
          title={tGeneral('staleAnalysisAlert.title')}
          description={tGeneral('staleAnalysisAlert.description')}
          actionLabel={tGeneral('staleAnalysisAlert.action')}
          actionDisabled={!areDocumentsIndexed}
          actionPending={isRetriggering}
          onActionClick={() => {
            retriggerComparison();
          }}
        />
      )}
    </>
  );
}
